/**
 * hide-product.js
 * - Gallery: image swap, arrow nav, thumb dots
 * - Video support: Shopify-hosted + YouTube / Vimeo embeds
 * - X-Ray: dots only visible on slide 0; toggle cards
 */

(function () {
  'use strict';

  var gallery    = document.querySelector('.hide-gallery');
  if (!gallery) return;

  var mainImg    = gallery.querySelector('.hide-gallery__main-img');
  var videoWrap  = gallery.querySelector('.hide-gallery__video-wrap');
  var thumbsEl   = gallery.querySelectorAll('.hide-thumb');
  var prevBtn    = gallery.querySelector('.hide-gallery__arrow--prev');
  var nextBtn    = gallery.querySelector('.hide-gallery__arrow--next');
  var thumbDots  = gallery.querySelectorAll('.hide-thumb-dot');
  var xrayBtn    = gallery.querySelector('.hide-xray-btn');
  var xrayDots   = gallery.querySelector('.hide-xray-dots');
  var dots       = gallery.querySelectorAll('.hide-dot');

  var thumbs       = Array.from(thumbsEl);
  var currentIndex = 0;

  /* ================================================================
     HELPERS
     ================================================================ */

  function setActiveThumb(idx) {
    thumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === idx); });
    Array.from(thumbDots).forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    gallery.dataset.slide = idx;
    currentIndex = idx;
    if (thumbs[idx]) {
      thumbs[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /** Show the static <img>, hide video wrap */
  function showImage(src, alt) {
    stopVideo();
    if (src) {
      // Clear srcset/sizes so the new src actually takes effect
      mainImg.srcset = '';
      mainImg.sizes  = '';
      mainImg.src    = src;
      mainImg.alt    = alt || '';
    }
    mainImg.hidden    = false;
    videoWrap.hidden  = true;
  }

  /** Inject and play a Shopify-hosted <video> */
  function showHostedVideo(src, type, alt) {
    stopVideo();
    videoWrap.innerHTML =
      '<video class="hide-gallery__video" src="' + src + '" ' +
      'type="' + (type || 'video/mp4') + '" ' +
      'controls autoplay playsinline muted ' +
      'aria-label="' + (alt || 'Product video') + '">' +
      '</video>';
    mainImg.hidden   = false; // keep as background placeholder
    videoWrap.hidden = false;
  }

  /** Inject a YouTube / Vimeo <iframe> */
  function showEmbedVideo(embedUrl) {
    stopVideo();
    videoWrap.innerHTML =
      '<iframe class="hide-gallery__embed" src="' + embedUrl + '" ' +
      'frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    mainImg.hidden   = true;
    videoWrap.hidden = false;
  }

  /** Tear down any active video */
  function stopVideo() {
    var vid = videoWrap.querySelector('video');
    if (vid) { vid.pause(); vid.src = ''; }
    videoWrap.innerHTML = '';
    videoWrap.hidden    = true;
    mainImg.hidden      = false;
  }

  /* ================================================================
     LOAD MEDIA (image or video) at index
     ================================================================ */

  function loadMedia(idx) {
    var thumb = thumbs[idx];
    if (!thumb) return;

    var mediaType = thumb.dataset.mediaType;
    var alt       = thumb.dataset.alt || '';

    setActiveThumb(idx);

    // Reset X-Ray state when leaving slide 0
    if (idx !== 0) {
      gallery.dataset.xray = 'false';
      xrayBtn && xrayBtn.setAttribute('aria-pressed', 'false');
      closeAllDots();
    }

    if (mediaType === 'video') {
      showHostedVideo(thumb.dataset.videoSrc, thumb.dataset.videoType, alt);

    } else if (mediaType === 'external_video') {
      showEmbedVideo(thumb.dataset.embedUrl);

    } else {
      // Plain image
      var newSrc = thumb.dataset.src;
      if (!newSrc) return;

      mainImg.classList.add('is-loading');
      var tmp = new Image();
      tmp.onload = function () {
        showImage(newSrc, alt);
        mainImg.classList.remove('is-loading');
      };
      tmp.onerror = function () {
        mainImg.classList.remove('is-loading');
      };
      tmp.src = newSrc;
    }
  }

  /* ================================================================
     THUMBNAIL CLICKS
     ================================================================ */

  thumbs.forEach(function (thumb, idx) {
    thumb.addEventListener('click', function () { loadMedia(idx); });
  });

  /* ================================================================
     ARROW NAVIGATION
     ================================================================ */

  if (prevBtn) {
    prevBtn.addEventListener('click', function () {
      loadMedia((currentIndex - 1 + thumbs.length) % thumbs.length);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', function () {
      loadMedia((currentIndex + 1) % thumbs.length);
    });
  }

  /* Keyboard */
  gallery.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowLeft')  loadMedia((currentIndex - 1 + thumbs.length) % thumbs.length);
    if (e.key === 'ArrowRight') loadMedia((currentIndex + 1) % thumbs.length);
  });

  /* Hide arrows when only 1 media item */
  if (thumbs.length <= 1) {
    if (prevBtn) prevBtn.hidden = true;
    if (nextBtn) nextBtn.hidden = true;
  }

  /* ================================================================
     X-RAY TOGGLE
     ================================================================ */

  if (xrayBtn) {
    xrayBtn.addEventListener('click', function () {
      var isOn = gallery.dataset.xray === 'true';
      gallery.dataset.xray = isOn ? 'false' : 'true';
      xrayBtn.setAttribute('aria-pressed', String(!isOn));
      if (isOn) closeAllDots();
    });
  }

  /* ================================================================
     DOT CARDS
     ================================================================ */

  dots.forEach(function (dot) {
    var trigger = dot.querySelector('.hide-dot__trigger');
    var card    = dot.querySelector('.hide-dot__card');
    if (!trigger) return;

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var wasOpen = dot.classList.contains('is-open');
      closeAllDots();
      if (!wasOpen) {
        dot.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        flipCardIfNeeded(dot, card);
      }
    });
  });

  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('.hide-dot'))) closeAllDots();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllDots();
  });

  function closeAllDots() {
    dots.forEach(function (d) {
      d.classList.remove('is-open');
      var t = d.querySelector('.hide-dot__trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  function flipCardIfNeeded(dot, card) {
    if (!card) return;
    dot.classList.remove('hide-dot--flip');
    var wrap    = gallery.querySelector('.hide-gallery__main-wrap');
    if (!wrap) return;
    var wrapRect = wrap.getBoundingClientRect();
    var dotRect  = dot.getBoundingClientRect();
    if (wrapRect.right - dotRect.right < (card.offsetWidth || 210) + 16) {
      dot.classList.add('hide-dot--flip');
    }
  }

})();
